// src/features/etf-info/components/EtfPeersTable.tsx
//
// Component Island (Island 5): competitor ETFs ranked by Assets Under
// Management. The backend does the heavy lifting (Polars sort by AUM
// descending), so this component just fetches and renders. Clicking a row
// navigates to that peer ETF's deep-dive.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchEtfPeers, type EtfPeer } from '../api';
import {
    formatMoney,
    formatCompactMoney,
    type CurrencyCode,
} from '../../../shared/utils/currency';

export const EtfPeersTable = ({
    symbol,
    currency = 'USD',
}: {
    symbol: string;
    currency?: CurrencyCode;
}) => {
    const [peers, setPeers] = useState<EtfPeer[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        fetchEtfPeers(symbol)
            .then((res) => {
                if (!cancelled) setPeers(res);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) setPeers([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    return (
        <div className="surface-card h-full flex flex-col">
            <h2 className="section-title mb-1">Competitor ETFs</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                Ranked by Assets Under Management
            </p>

            <div className="flex-1 overflow-auto min-h-[300px]">
                {isLoading ? (
                    <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse text-sm">
                        Ranking peers…
                    </div>
                ) : peers.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
                        No peer data available.
                    </div>
                ) : (
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="text-xs uppercase bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 sticky top-0 z-10">
                            <tr>
                                <th className="p-3 font-semibold">Symbol</th>
                                <th className="p-3 font-semibold text-right">Price</th>
                                <th className="p-3 font-semibold text-right">AUM</th>
                                <th className="p-3 font-semibold text-right">Expense</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {peers.map((p) => {
                                const isCurrent = p.symbol === symbol;
                                const change = p.changes_percentage ?? 0;
                                const isPos = change >= 0;
                                return (
                                    <tr
                                        key={p.symbol}
                                        onClick={() =>
                                            navigate(
                                                `/dashboard/etf-info-page?symbol=${p.symbol}`,
                                            )
                                        }
                                        className={`cursor-pointer transition-colors ${
                                            isCurrent
                                                ? 'bg-blue-50 dark:bg-blue-900/20'
                                                : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                        }`}
                                    >
                                        <td className="p-3">
                                            <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                                {p.symbol}
                                                {isCurrent && (
                                                    <span className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded font-bold">
                                                        THIS
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[120px]"
                                                title={p.name}
                                            >
                                                {p.name}
                                            </div>
                                        </td>
                                        <td className="p-3 text-right font-mono">
                                            <div className="text-slate-900 dark:text-slate-200">
                                                {formatMoney(p.price ?? 0, {
                                                    explicitCurrency: currency,
                                                })}
                                            </div>
                                            <div
                                                className={`text-xs ${
                                                    isPos
                                                        ? 'text-emerald-500'
                                                        : 'text-red-500'
                                                }`}
                                            >
                                                {isPos ? '+' : ''}
                                                {change.toFixed(2)}%
                                            </div>
                                        </td>
                                        <td className="p-3 text-right font-mono font-medium text-slate-700 dark:text-slate-300">
                                            {formatCompactMoney(
                                                p.aum,
                                                { explicitCurrency: currency },
                                                { fallback: 'N/A' },
                                            )}
                                        </td>
                                        <td className="p-3 text-right font-mono text-slate-500 dark:text-slate-400">
                                            {p.expense_ratio != null
                                                ? `${p.expense_ratio.toFixed(2)}%`
                                                : 'N/A'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default EtfPeersTable;
